import { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  House, GitBranch, Warning, Flask, FileText, Gear,
  ChatCircleDots, MagnifyingGlass, SignOut, Brain, Bell, FolderOpen
} from "@phosphor-icons/react";
import ChatDrawer from "@/components/ChatDrawer";
import GlobalSearch from "@/components/GlobalSearch";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Home Overview", icon: House, testid: "nav-home", end: true },
  { to: "/processes", label: "Process Orchestration", icon: GitBranch, testid: "nav-processes" },
  { to: "/insights", label: "AI Insights & Alerts", icon: Warning, testid: "nav-insights" },
  { to: "/simulation", label: "Simulation Lab", icon: Flask, testid: "nav-simulation" },
  { to: "/reports", label: "Reports & Recommendations", icon: FileText, testid: "nav-reports" },
  { to: "/media", label: "Media & Files", icon: FolderOpen, testid: "nav-media" },
  { to: "/admin", label: "Administration", icon: Gear, testid: "nav-admin" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [chatOpen, setChatOpen] = useState(false);
  const location = useLocation();
  const nav = useNavigate();

  const currentPageLabel =
    NAV.find((n) => (n.end ? location.pathname === n.to : location.pathname.startsWith(n.to)))?.label || "ITSME";

  return (
    <div className="flex min-h-screen bg-[#090A0E] text-[#F3F4F6] relative z-10">
      {/* Sidebar */}
      <aside className="w-64 shrink-0 border-r border-[#272A35] bg-[#0B0D12] flex flex-col">
        <div className="px-5 py-5 flex items-center gap-2 border-b border-[#272A35]">
          <div className="w-8 h-8 rounded-md grid place-items-center"
               style={{ background: "linear-gradient(135deg, #0055FF, #00E5FF)" }}>
            <Brain size={18} weight="bold" color="#0B0D12" />
          </div>
          <div>
            <div className="font-heading font-bold text-base tracking-tight leading-none">ITSME</div>
            <div className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mt-1">Agentic Ops</div>
          </div>
        </div>

        <nav className="p-3 flex-1 space-y-1">
          <div className="data-label px-3 py-2">Console</div>
          {NAV.map(({ to, label, icon: Icon, testid, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                `group flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? "bg-[#13151A] text-[#F3F4F6] border-l-2 border-[#00E5FF] pl-[10px]"
                    : "text-slate-400 hover:bg-[#13151A] hover:text-slate-200"
                }`
              }
            >
              <Icon size={17} weight={location.pathname === to ? "fill" : "regular"} />
              <span className="truncate">{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-[#272A35]">
          <div className="itsme-card p-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#0055FF] grid place-items-center text-xs font-bold uppercase">
                {(user?.name || user?.email || "U").slice(0, 1)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm truncate">{user?.name || "User"}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">{user?.role}</div>
              </div>
              <button
                data-testid="logout-btn"
                onClick={async () => { await logout(); nav("/login"); }}
                className="p-1.5 rounded hover:bg-[#1C1F26] text-slate-400"
                title="Sign out"
              >
                <SignOut size={16} />
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-14 shrink-0 border-b border-[#272A35] bg-[#0B0D12]/70 backdrop-blur-md flex items-center px-6 gap-4 sticky top-0 z-20">
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span className="text-slate-500">Console</span>
            <span>/</span>
            <span className="text-slate-200">{currentPageLabel}</span>
          </div>
          <div className="ml-6 flex-1 max-w-md">
            <GlobalSearch />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-[#1C1F26]"
                    data-testid="alerts-btn">
              <Bell size={16} />
            </Button>
            <Button
              data-testid="open-ai-chat"
              onClick={() => setChatOpen(true)}
              size="sm"
              className="bg-[#13151A] border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10"
            >
              <ChatCircleDots size={16} className="mr-2" /> Chat with AI
            </Button>
          </div>
        </header>

        <main className="flex-1 p-6 min-w-0 overflow-auto">
          <Outlet />
        </main>
      </div>

      <ChatDrawer open={chatOpen} onOpenChange={setChatOpen} page={currentPageLabel} />
    </div>
  );
}
